import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { AuthLayout, GoogleIcon, startGoogleLogin } from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { REGISTER, AUTH } from "@/constants/testIds";

export default function RegisterPage() {
  const { register, user, loading } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("La password deve avere almeno 6 caratteri");
    if (password !== confirm) return toast.error("Le password non coincidono");
    setBusy(true);
    try {
      await register(name, email, password);
      toast.success("Account creato. Benvenuto!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(errorMessage(err, "Registrazione non riuscita"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Crea il tuo account" subtitle="Unisciti al team e inizia a documentare i progressi dei pazienti.">
      <Button type="button" variant="secondary" className="w-full h-11 justify-center gap-2" onClick={startGoogleLogin} data-testid={AUTH.googleButton}>
        <GoogleIcon /> Registrati con Google
      </Button>
      <div className="flex items-center gap-3 my-6">
        <Separator className="flex-1" />
        <span className="text-xs text-muted-foreground uppercase tracking-wider">oppure</span>
        <Separator className="flex-1" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nome e cognome</Label>
          <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Dott.ssa Maria Rossi" className="h-11 bg-card" data-testid={REGISTER.nameInput} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@clinica.it" className="h-11 bg-card" data-testid={REGISTER.emailInput} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-card" data-testid={REGISTER.passwordInput} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Conferma</Label>
            <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11 bg-card" data-testid={REGISTER.passwordConfirmInput} />
          </div>
        </div>
        <Button type="submit" className="w-full h-11" disabled={busy} data-testid={REGISTER.submitButton}>
          {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Crea account
        </Button>
      </form>
      <p className="text-sm text-muted-foreground mt-8 text-center">
        Hai già un account?{" "}
        <Link to="/login" className="text-primary font-medium hover:underline" data-testid={REGISTER.loginLink}>Accedi</Link>
      </p>
    </AuthLayout>
  );
}
